import useSWR from "swr";
import { fetchCategories, fetchChannel, formatDate, formatNumber } from "@/lib/api";
import { Github, MapPin, Youtube } from "lucide-react";
import SyncWidget from "@/components/SyncWidget";
import usePageMeta from "@/hooks/use-page-meta";

export default function About() {
  const { data: channel } = useSWR("/channel", fetchChannel);
  const { data: cats } = useSWR("/videos/categories", fetchCategories);
  usePageMeta({
    title: "About the tobiasdeamon Video Archive",
    description:
      "About the tobiasdeamon Vault — a curated archive of every video from the channel, since June 2012. Search, browse, watch, step away.",
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="about-channel-section">
      <div className="mono-label">Colophon</div>
      <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
        About this archive.
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-14">
        <div className="lg:col-span-2 space-y-8">
          <p className="text-white/70 text-lg leading-relaxed font-sans">
            This is an unofficial, curated archive of the {channel?.title || "tobiasdeamon"} YouTube channel — a
            catalogue of every upload since <span className="text-[#d95d39]">{formatDate(channel?.published_at) || "June 2012"}</span>.
            The archive is intentionally quieter than YouTube: no autoplay, no rabbit hole, no algorithmic push.
            Just search, browse, watch, and step away.
          </p>

          <p className="text-white/60 leading-relaxed font-sans">
            {channel?.description || ""}
          </p>

          <div>
            <div className="mono-label mb-3">Categories in the vault</div>
            <div className="flex flex-wrap gap-2">
              {cats?.categories?.map((c) => (
                <span
                  key={c.name}
                  className="px-3 py-1.5 rounded-full text-xs font-mono-tech uppercase tracking-widest bg-white/[0.04] border border-white/10 text-white/70"
                >
                  {c.name} <span className="text-white/30 ml-1">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <SyncWidget />
          <div className="rounded-2xl border border-white/10 bg-[#12141d] overflow-hidden">
            {channel?.banner && (
              <div className="aspect-[4/1] bg-black">
                <img src={channel.banner} alt="channel banner" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5 flex gap-4 items-center">
              {channel?.thumbnail && (
                <img
                  src={channel.thumbnail}
                  alt={channel.title}
                  className="w-14 h-14 rounded-full border border-white/10"
                />
              )}
              <div>
                <div className="font-serif-editorial text-2xl text-[#f4f1ea]">{channel?.title}</div>
                <div className="mono-label text-white/40 mt-1">{channel?.handle}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 border-t border-white/10 text-center">
              {[
                { l: "Subs", v: formatNumber(channel?.subscriber_count) },
                { l: "Views", v: formatNumber(channel?.view_count) },
                { l: "Videos", v: formatNumber(channel?.video_count) },
              ].map((s) => (
                <div key={s.l} className="p-4 border-r border-white/10 last:border-r-0">
                  <div className="font-serif-editorial text-xl text-[#f4f1ea]">{s.v}</div>
                  <div className="mono-label text-white/40 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-white/10 space-y-3 text-sm">
              {channel?.country && (
                <div className="flex items-center gap-2 text-white/60">
                  <MapPin className="w-4 h-4 text-[#d95d39]" /> {channel.country}
                </div>
              )}
              <a
                href={channel?.handle ? `https://www.youtube.com/${channel.handle}` : "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-[#d95d39] hover:underline"
              >
                <Youtube className="w-4 h-4" /> Visit on YouTube
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
